import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { queryClient } from '../providers/QueryProvider';

const YEAR_KEY = '@sms_selected_year';
const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = CURRENT_YEAR - 10;
const MAX_YEAR = CURRENT_YEAR + 10;

// Module-level variable for non-React access (e.g., axios interceptor)
let _currentYear = CURRENT_YEAR;

/** Get the currently selected year (callable outside React components) */
export function getSelectedYear(): number {
  return _currentYear;
}

// --- Web URL helpers --------------------------------------------------------
// On web we keep `?year=` in the URL so refresh + share-link survive. Native
// platforms have no URL to sync with, so these no-op there.

function readYearFromUrl(): number | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    const param = new URL(window.location.href).searchParams.get('year');
    if (!param) return null;
    const parsed = parseInt(param, 10);
    if (isNaN(parsed) || parsed < MIN_YEAR || parsed > MAX_YEAR) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeYearToUrl(year: number): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('year') === String(year)) return;
    url.searchParams.set('year', String(year));
    window.history.replaceState(null, '', url.toString());
  } catch {
    // History API can throw in sandboxed iframes — swallow; fallback persistence
    // (AsyncStorage) still applies.
  }
}

interface YearContextType {
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  minYear: number;
  maxYear: number;
}

const YearContext = createContext<YearContextType | undefined>(undefined);

interface YearProviderProps {
  children: ReactNode;
}

export const YearProvider: React.FC<YearProviderProps> = ({ children }) => {
  const [selectedYear, setSelectedYearState] = useState<number>(CURRENT_YEAR);

  useEffect(() => {
    const loadYear = async () => {
      try {
        // Hydration order: URL ?year= (web only) → AsyncStorage → CURRENT_YEAR.
        // URL wins so a hard refresh on `/strategy?year=2024` rehydrates that
        // year regardless of what was last persisted.
        const fromUrl = readYearFromUrl();
        if (fromUrl != null) {
          setSelectedYearState(fromUrl);
          _currentYear = fromUrl;
          // Mirror to AsyncStorage so the next session without a URL param
          // remembers this choice.
          try { await AsyncStorage.setItem(YEAR_KEY, fromUrl.toString()); } catch {}
          return;
        }
        const saved = await AsyncStorage.getItem(YEAR_KEY);
        if (saved) {
          const parsed = parseInt(saved, 10);
          if (!isNaN(parsed) && parsed >= MIN_YEAR && parsed <= MAX_YEAR) {
            setSelectedYearState(parsed);
            _currentYear = parsed;
            // Reflect in URL so deep-link copy works without first picking
            // the dropdown.
            writeYearToUrl(parsed);
          }
        }
      } catch (error) {
        console.error('Error loading year:', error);
      }
    };
    loadYear();
  }, []);

  const setSelectedYear = async (year: number) => {
    if (year >= MIN_YEAR && year <= MAX_YEAR) {
      setSelectedYearState(year);
      _currentYear = year;
      writeYearToUrl(year);
      try {
        await AsyncStorage.setItem(YEAR_KEY, year.toString());
      } catch (error) {
        console.error('Error saving year:', error);
      }
      // Year drives every `['strategy', ...]` query key. Invalidating only
      // that family avoids torching unrelated caches (notifications,
      // comments, profile, etc.) that don't depend on year.
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
    }
  };

  return (
    <YearContext.Provider value={{ selectedYear, setSelectedYear, minYear: MIN_YEAR, maxYear: MAX_YEAR }}>
      {children}
    </YearContext.Provider>
  );
};

export function useYear() {
  const context = useContext(YearContext);
  if (!context) {
    throw new Error('useYear must be used within a YearProvider');
  }
  return context;
}
